import { useMemo, useState, type ReactNode } from 'react'
import { Dialog } from './Dialog'
import { Icon } from './Icon'
import { useMediaQuery } from '../hooks/useMediaQuery'
import { TOUCH_SCREEN_QUERY } from '../lib/platform'

export interface FilterOption {
  id: string
  label: string
}

interface Props {
  title: string
  options: FilterOption[]
  /** The option chosen now ('' for the first, "All"/"Anyone"/"Any time"). */
  chosen: string
  onPick: (id: string) => void
  onClose: () => void
  /** A long list (conversations, people) gets a field to find one by name. */
  searchLabel?: string
  /** Below the options: Pick dates, on the time page. */
  children?: ReactNode
}

/**
 * One of Search's filters, as a phone page: Cancel, the title, and the choices, the chosen one ticked; a long list can be
 * searched. On a touch screen the field doesn't take the cursor as it opens, so the keyboard doesn't cover the list.
 */
export function FilterPage({ title, options, chosen, onPick, onClose, searchLabel, children }: Props) {
  const touch = useMediaQuery(TOUCH_SCREEN_QUERY)
  const [query, setQuery] = useState('')
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase()
    return q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options
  }, [options, query])

  return (
    <Dialog chrome="bar" open onClose={onClose} title={title} titleId="filter-page-title" closeLabel="Cancel" className="filter-page slides-up">
      {searchLabel ? (
        <div className="filter-page-search">
          <div className="search-field">
            <Icon name="search" />
            <input
              type="search"
              aria-label={searchLabel}
              placeholder={searchLabel}
              autoComplete="off"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-autofocus={touch ? undefined : true}
            />
          </div>
        </div>
      ) : null}
      <div className="dialog-body filter-page-body">
        <ul className="filter-options">
          {shown.map((option) => (
            <li key={option.id}>
              <button type="button" className={option.id === chosen ? 'filter-option is-chosen' : 'filter-option'} onClick={() => onPick(option.id)}>
                <span>{option.label}</span>
                {option.id === chosen ? <Icon name="check" /> : null}
              </button>
            </li>
          ))}
        </ul>
        {shown.length === 0 ? <p className="notice">Nothing called “{query.trim()}”.</p> : null}
        {children}
      </div>
    </Dialog>
  )
}
